import { cn } from '@viapeople/components'
import type { ReactNode } from 'react'

export interface TableColumn<T extends object> {
	field: Extract<keyof T, string>
	label?: string
	width?: string
	renderHeader?: () => ReactNode
	render?: (item: T, index?: number) => ReactNode
}

interface TableProps<T extends object> {
	columns: TableColumn<T>[]
	data: T[]
	className?: string
	headerClassName?: string
	rowClassName?: string | ((item: T) => string)
	bodyClassName?: string
	noBorder?: boolean
	onRowClick?: (item: T) => void
}

export function Table<T extends object>({
	columns,
	data,
	className,
	headerClassName,
	rowClassName,
	bodyClassName,
	noBorder,
	onRowClick,
}: TableProps<T>) {
	const gridTemplateColumns = columns.map((col) => col.width || '1fr').join(' ')

	return (
		<div className={cn('bg-neutral-0 rounded-lg', !noBorder && 'border border-neutral-200', className)}>
			<div className={cn('grid border-b border-neutral-100', headerClassName)} style={{ gridTemplateColumns }}>
				{columns.map((column, index) => (
					<div key={`${column.field}-${index}`} className='py-3 px-4 text-neutral-500 capitalize flex items-center'>
						{column.renderHeader ? column.renderHeader() : column.label}
					</div>
				))}
			</div>

			<div className={cn('min-h-0', bodyClassName)}>
				{data.map((item, rowIndex) => (
					// biome-ignore lint/a11y/noStaticElementInteractions: role is conditionally set based on onRowClick prop
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: generic table data has no guaranteed stable identifier
						key={rowIndex}
						className={cn(
							'grid border-b border-neutral-100 last:border-b-0',
							typeof rowClassName === 'function' ? rowClassName(item) : rowClassName,
							onRowClick && 'cursor-pointer hover:bg-neutral-50',
						)}
						role={onRowClick ? 'button' : undefined}
						tabIndex={onRowClick ? 0 : undefined}
						style={{ gridTemplateColumns }}
						onClick={onRowClick ? () => onRowClick(item) : undefined}
						onKeyDown={
							onRowClick
								? (e) => {
										if (e.key === 'Enter' || e.key === ' ') onRowClick(item)
									}
								: undefined
						}>
						{columns.map((column) => (
							<div key={column.field as string} className='py-3 px-4 flex items-center'>
								{column.render ? column.render(item, rowIndex) : String(item[column.field])}
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	)
}
